import React, { useEffect, useState, useCallback } from "react";
import { Activity, RefreshCw, Car, UtensilsCrossed, MapPin, Phone, User, XCircle, Loader2, List, Map as MapIcon, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import LiveOrdersMap from "../../components/app/LiveOrdersMap";
import { adminApi, errorMessage, fmtDateTime, titleCase, inr } from "../../lib/api";
import AdminUserDialog from "../../components/admin/AdminUserDialog";
import AdminOrderDetailDialog from "../../components/admin/AdminOrderDetailDialog";

const TABS = [
  { key: "rides", label: "Rides", icon: Car },
  { key: "deliveries", label: "Deliveries", icon: UtensilsCrossed },
  { key: "private_cars", label: "Private cars", icon: Car },
];
const KIND = { rides: "ride", deliveries: "delivery", private_cars: "private_car" };

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return <p className="text-sm text-ink-soft"><span className="text-ink-muted">{label}:</span> {value}</p>;
}

function NameBtn({ id, onOpen, children, testid }) {
  if (!id) return <>{children}</>;
  return <button onClick={() => onOpen(id)} className="font-semibold text-ink hover:text-brand hover:underline" data-testid={testid}>{children}</button>;
}

function OrderCard({ item, tabKey, onCancel, onResolve, onComplete, onOpenProfile, onOpenDetail }) {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm("Cancel this order as 'No driver available'? The customer will be notified and any prepaid amount refunded.")) return;
    setBusy(true);
    try { await onCancel(KIND[tabKey], item.id); } finally { setBusy(false); }
  };
  const complete = async () => {
    if (!window.confirm("Complete this live ride on the customer's/driver's request (GPS/technical issue)? If the online payment wasn't confirmed, the ride is marked completed and the payment stays pending for separate settlement.")) return;
    setBusy(true);
    try { await onComplete(item.id); } finally { setBusy(false); }
  };
  const resolve = async (action) => {
    const msg = action === "confirm_paid"
      ? "Confirm the customer's payment arrived and settle this ride? (Completes a taxi trip / dispatches a goods booking.)"
      : "Cancel this ride and refund any captured online payment? The customer will be notified.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { await onResolve(item.id, action); } finally { setBusy(false); }
  };
  return (
    <div className="kt-card p-5" data-testid={`live-${item.type}-${item.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-ink">
            <span className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold text-brand">{titleCase(item.status || "")}</span>
            {item.payment_stuck && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700" data-testid={`live-payment-stuck-${item.id}`}>⚠ Payment stuck{item.payment_status ? ` · ${titleCase(item.payment_status)}` : ""}</span>}
            <span className="font-mono text-xs text-ink-muted" data-testid={`live-order-number-${item.id}`}>{item.order_number || String(item.id).slice(-6).toUpperCase()}</span>
          </p>
          <Row label={<><User className="mb-0.5 mr-1 inline h-3 w-3" />Customer</>} value={<><NameBtn id={item.customer_user_id} onOpen={onOpenProfile} testid={`live-customer-${item.id}`}>{item.customer_name || "—"}</NameBtn>{item.customer_phone ? ` · ${item.customer_phone}` : ""}</>} />
          {item.type === "delivery" && <Row label="Restaurant" value={<NameBtn id={item.restaurant_owner_id} onOpen={onOpenProfile} testid={`live-restaurant-${item.id}`}>{item.restaurant_name}</NameBtn>} />}
          <Row label="Driver" value={item.driver_name ? <><NameBtn id={item.driver_user_id} onOpen={onOpenProfile} testid={`live-driver-${item.id}`}>{item.driver_name}</NameBtn>{item.driver_phone ? ` · ${item.driver_phone}` : ""}</> : "Not assigned yet"} />
          {item.type === "ride" && <><Row label="Pickup" value={item.pickup_address} /><Row label="Drop" value={item.drop_address} /></>}
          {item.type === "delivery" && <Row label="Deliver to" value={item.delivery_address} />}
          {item.type === "private_car" && <><Row label="Pickup" value={item.pickup_address} /><Row label="Destination" value={item.drop_address} /><Row label="Days" value={item.days} /></>}
          <Row label="Amount" value={item.fare != null ? inr(item.fare) : item.total != null ? inr(item.total) : null} />
          <p className="pt-1 text-xs text-ink-muted">{fmtDateTime(item.created_at)}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button size="sm" variant="outline" onClick={() => onOpenDetail({ kind: item.type, id: item.id })} className="rounded-full" data-testid={`live-view-${item.id}`}>
            <Eye className="mr-2 h-4 w-4" /> View details
          </Button>
          {(item.driver_lat != null && item.driver_lng != null) && (
            <a href={`https://maps.google.com/?q=${item.driver_lat},${item.driver_lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-surface-200 px-3 py-1 text-xs font-semibold text-ink-soft hover:bg-surface-100" data-testid={`live-driver-map-${item.id}`}><MapPin className="h-3 w-3" /> Driver on map</a>
          )}
          {item.customer_phone && <a href={`tel:${item.customer_phone}`} className="inline-flex items-center gap-1 rounded-full border border-surface-200 px-3 py-1 text-xs font-semibold text-ink-soft hover:bg-surface-100"><Phone className="h-3 w-3" /> Call customer</a>}
          {item.type === "ride" && item.payment_stuck ? (
            <>
              <Button size="sm" onClick={() => resolve("confirm_paid")} disabled={busy} className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`live-confirm-paid-${item.id}`}>
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Confirm paid & settle
              </Button>
              <Button size="sm" variant="outline" onClick={() => resolve("cancel_refund")} disabled={busy} className="rounded-full border-red-300 text-red-600 hover:bg-red-50" data-testid={`live-cancel-refund-${item.id}`}>
                <XCircle className="mr-2 h-4 w-4" /> Cancel &amp; refund
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={cancel} disabled={busy} className="rounded-full border-red-300 text-red-600 hover:bg-red-50" data-testid={`live-cancel-${item.id}`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="mr-2 h-4 w-4" />} Cancel · no driver
            </Button>
          )}
          {item.type === "ride" && ["driver_assigned", "driver_arriving", "in_progress", "payment_pending"].includes(item.status) && (
            <Button size="sm" variant="outline" onClick={complete} disabled={busy} className="rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`live-complete-${item.id}`}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Complete trip
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LiveOrders() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("rides");
  const [view, setView] = useState("list");
  const [picked, setPicked] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);
  const [detailOrder, setDetailOrder] = useState(null);

  const load = useCallback((silent) => {
    if (!silent) setData(null);
    adminApi.liveOrders().then(setData).catch((e) => { toast.error(errorMessage(e)); setData({ rides: [], deliveries: [], private_cars: [], counts: {} }); });
  }, []);

  useEffect(() => {
    load();
    // Fast pin refresh in map view so ops watch trips move; slower in list view.
    const t = setInterval(() => load(true), view === "map" ? 5000 : 15000);
    return () => clearInterval(t);
  }, [load, view]);

  const onCancel = useCallback(async (kind, id) => {
    try {
      await adminApi.cancelLiveOrder(kind, id, "No driver available");
      toast.success("Cancelled — customer notified");
      setPicked(null);
      load(true);
    } catch (e) { toast.error(errorMessage(e)); }
  }, [load]);

  const onResolve = useCallback(async (rideId, action) => {
    try {
      await adminApi.resolveRidePayment(rideId, action, action === "cancel_refund" ? "Stuck payment resolved by support" : null);
      toast.success(action === "confirm_paid" ? "Payment confirmed — ride settled" : "Ride cancelled & refund started");
      setPicked(null);
      load(true);
    } catch (e) { toast.error(errorMessage(e)); }
  }, [load]);

  const onComplete = useCallback(async (rideId) => {
    try {
      await adminApi.completeLiveRide(rideId, "Completed by support (GPS/technical issue)");
      toast.success("Ride completed — if payment wasn't confirmed it stays pending for settlement");
      setPicked(null);
      load(true);
    } catch (e) { toast.error(errorMessage(e)); }
  }, [load]);

  const counts = data?.counts || {};
  const list = data?.[tab] || [];
  const allItems = data ? [...(data.rides || []), ...(data.deliveries || []), ...(data.private_cars || [])] : [];

  return (
    <>
      <PageHeader title="Live orders" description="Every ongoing ride, delivery and private-car job. Auto-refreshes every 15s." actions={
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-surface-300 p-0.5">
            <button onClick={() => setView("list")} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${view === "list" ? "bg-ink text-white" : "text-ink-soft"}`} data-testid="live-view-list"><List className="h-3.5 w-3.5" /> List</button>
            <button onClick={() => setView("map")} className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ${view === "map" ? "bg-ink text-white" : "text-ink-soft"}`} data-testid="live-view-map"><MapIcon className="h-3.5 w-3.5" /> Map</button>
          </div>
          <Button variant="outline" onClick={() => load()} className="rounded-full" data-testid="live-orders-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
        </div>
      } />

      {view === "map" ? (
        <div className="space-y-4">
          <LiveOrdersMap items={allItems} onPick={setPicked} />
          {picked && (
            <div data-testid="live-map-picked">
              <OrderCard item={picked} tabKey={picked.type === "ride" ? "rides" : picked.type === "delivery" ? "deliveries" : "private_cars"} onCancel={onCancel} onResolve={onResolve} onComplete={onComplete} onOpenProfile={setProfileUserId} onOpenDetail={setDetailOrder} />
            </div>
          )}
          {data && allItems.length === 0 && <EmptyState icon={Activity} title="No live orders" text="Nothing is live right now." />}
        </div>
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`text-left ${tab === t.key ? "ring-2 ring-brand rounded-2xl" : ""}`} data-testid={`live-tab-${t.key}`}>
                <StatCard label={t.label} value={counts[t.key] ?? "…"} icon={t.icon} tone={tab === t.key ? "brand" : "graphite"} />
              </button>
            ))}
          </div>
          {data === null && <LoadingBlock rows={4} />}
          {data && list.length === 0 && <EmptyState icon={Activity} title={`No ongoing ${TABS.find((t) => t.key === tab)?.label.toLowerCase()}`} text="Nothing is live right now." />}
          {data && list.length > 0 && (
            <div className="space-y-4" data-testid="live-orders-list">
              {list.map((item) => <OrderCard key={item.id} item={item} tabKey={tab} onCancel={onCancel} onResolve={onResolve} onComplete={onComplete} onOpenProfile={setProfileUserId} onOpenDetail={setDetailOrder} />)}
            </div>
          )}
        </>
      )}
      <AdminUserDialog userId={profileUserId} onClose={() => setProfileUserId(null)} />
      <AdminOrderDetailDialog order={detailOrder} onClose={() => setDetailOrder(null)} />
    </>
  );
}
